import { useEffect, useRef, useState } from 'react';
const PROOF_REFRESH_MS = 4 * 60 * 1000;
import { getPaymentProof } from '../../api/endpoints/payments';
export function usePaymentProof(
  paymentId: number,
  available: boolean,
  imageProof: boolean,
  lazy = false,
  loadProof = getPaymentProof,
) {
  const [proof, setProof] = useState<{
    state: 'none' | 'loading' | 'ready' | 'error';
    url?: string;
  }>({ state: 'none' });
  const request = useRef<() => Promise<string | undefined>>(async () => undefined);
  useEffect(() => {
    let active = true;
    let refreshTimer: ReturnType<typeof setTimeout> | undefined;
    let expiryTimer: ReturnType<typeof setTimeout> | undefined;
    let hasReadyProof = false;
    let cachedUrl: string | undefined;
    let validUntil = 0;
    let pending: Promise<string | undefined> | undefined;
    request.current = async () => undefined;
    setProof({ state: 'none' });
    if (!available) {
      setProof({ state: 'none' });
      return () => {
        active = false;
      };
    }
    const load = async () => {
      if (!hasReadyProof) setProof({ state: 'loading' });
      try {
        const expiresAt = Date.now() + 5 * 60 * 1000;
        const url = await loadProof(paymentId);
        if (!active) return;
        if (hasReadyProof && imageProof) {
          await new Promise<void>((resolve, reject) => {
            const image = new Image();
            image.onload = () => resolve();
            image.onerror = () => reject(new Error('Proof image could not be loaded'));
            image.src = url;
          });
          if (!active) return;
        }
        if (Date.now() >= expiresAt) throw new Error('Proof link expired during loading');
        if (expiryTimer) clearTimeout(expiryTimer);
        setProof({ state: 'ready', url });
        hasReadyProof = true;
        cachedUrl = url;
        validUntil = expiresAt;
        expiryTimer = setTimeout(() => {
          if (active) {
            hasReadyProof = false;
            cachedUrl = undefined;
            setProof({ state: lazy ? 'none' : 'error' });
          }
        }, expiresAt - Date.now());
        if (!lazy) refreshTimer = setTimeout(load, PROOF_REFRESH_MS);
        return url;
      } catch {
        if (active && !hasReadyProof) setProof({ state: 'error' });
      }
    };
    request.current = () => {
      if (cachedUrl && Date.now() < validUntil) return Promise.resolve(cachedUrl);
      if (!pending)
        pending = load().finally(() => {
          pending = undefined;
        });
      return pending;
    };
    if (!lazy) void load();
    return () => {
      active = false;
      if (refreshTimer) clearTimeout(refreshTimer);
      if (expiryTimer) clearTimeout(expiryTimer);
    };
  }, [paymentId, available, imageProof, lazy, loadProof]);

  return { ...proof, load: () => request.current() };
}
